import { Nullable } from '../common/Nullable'
import { UserConsentState } from '../components/Consent/model'
import { MeetingLanguage } from './Room'
import { BusinessEventType } from './BusinessEvent'
import type { FileInfo } from './File'

export type AnonymousAccessType = 'allow' | 'deny'
export type ConsentUploadSetting = 'required' | 'optional'

export type AccountRegion = 'en-US' | 'en-CA'

export const ACCOUNT_REGION_OPTIONS: { id: AccountRegion; label: string }[] = [
  { id: 'en-US', label: 'United States' },
  { id: 'en-CA', label: 'Canada' },
]

export type Account = {
  name: string
  tenantType: string
  parentId?: string
  config: AccountConfig
  enterpriseConfig: EnterpriseAccountConfig
  effectiveConfig: EffectiveAccountConfig
  createdAt: string
  updatedAt: string
  metadata?: { [key: string]: unknown }
  residency: string
}

export const getAccountCRMIntegrationConfig = (account: Account | undefined, serviceId: string): CRMAccountIntegrationConfig | null => {
  const config = account?.config.integrations.find((config) => config.type === 'crm' && config.serviceId === serviceId) ?? null
  return config ? (config as CRMAccountIntegrationConfig) : null
}

export const getEffectiveAccountIntegrationConfig = (account: Account | undefined, serviceId: string): AccountIntegrationConfig | null => {
  return account?.effectiveConfig.integrations?.find((config) => config.serviceId === serviceId) || null
}

export const getAccountDocumentIntegrationConfig = (serviceId: string, account?: Account): DocumentAccountIntegrationConfig | null => {
  const config = account?.config.integrations.find((config) => config.type === 'document' && config.serviceId === serviceId) ?? null
  return config ? (config as DocumentAccountIntegrationConfig) : null
}

export type LogoConfig = {
  logoFile: FileInfo
}

export type AccountConfig = {
  anonymousAccess?: AnonymousAccessType
  region?: AccountRegion
  integrations: AccountIntegrationConfig[]
  restrictedSessionDelete: boolean
  complianceNotice?: ComplianceNotice
  aiMeetingTemplateCalculator: boolean
  aiMeetingTitleCalculator: boolean
  userConsentTrackingEnabled: boolean
  userConsentManualOverride: boolean
  consentApprovalCheck: boolean
  userConsentManualConfigs: UserConsentManualConfigs
  piiRedactionCategories: string[]
  piiRedactionEntities: string[]
  sentimentAnalysis: boolean
  clientProfileOnDemandCRMExecution: boolean
  assistantMeetings: boolean
  email: EmailConfig
  exportConfirmation: ExportConfirmationConfig
  meetingScorecard: boolean
  crossAccountAccess: boolean
  inviteLinkAutoRenewal: boolean
  crossClientQueryUserExecution: boolean
  assets?: {
    logo?: LogoConfig
  }
  privateMeetings: boolean
  multiLanguageSupport: MultiLanguageSupportConfig
  meetingPrepEmail: MeetingPrepEmailConfig
  meetingProcessedDoneEmail: MeetingProcessedDoneEmailConfig
  dataRetentionWarningEmail: DataRetentionWarningEmailConfig
  crossClientEvaluationEmail: CrossClientEvaluationEmailConfig
  businessEvent: boolean
  emailBusinessEvent: boolean
  businessEventExport: boolean
  downloadTranscriptAvailable: boolean
  integrationExportComplianceMessage: IntegrationExportComplianceMessageConfig
  emailComplianceMessage: EmailComplianceMessageConfig
  pdfComplianceMessage: PdfComplianceMessageConfig
  copyComplianceMessage: CopyComplianceMessageConfig
  inPersonComplianceMessage: InPersonComplianceMessageConfig
  restrictedSessionTrimming: boolean
}

export type AccountUpdateOptions = {
  changeExistingPrivateMeetingsToPublic?: boolean
}

export type ExportConfirmationConfig = {
  integrationEnabled: boolean
  documentEnabled: boolean
  message: string
}

export type IntegrationExportComplianceMessageConfig = {
  message: string
  enabled: boolean
}

export type EmailComplianceMessageConfig = {
  message: string
  enabled: boolean
}

export const LOCK_USER_OVERRIDE = 'lock_user_override'
export const LOCK_ALL_OVERRIDE = 'lock_all_override'

export type PdfComplianceMessageConfig = {
  message: string
  enabled: boolean
}

export type CopyComplianceMessageConfig = {
  message: string
  enabled: boolean
}

export type InPersonComplianceMessageConfig = {
  message?: string
  enabled: boolean
}

export type EnterpriseAccountConfig = {
  integrationsConfig: IntegrationsEnterpriseAccountConfig
  oauthAppsConfig: OAuthAppsEnterpriseAccountConfig
  meetingSummaryConfig: MeetingSummaryEnterpriseAccountConfig
  complianceConfig: ComplianceAccountConfig
  piiRedactionCategories?: string[]
  piiRedactionEntities?: string[]
  restrictedSessionDelete?: boolean
  restrictedSessionDeleteGlobal?: boolean
  restrictedSessionTrimming?: boolean
  downloadTranscriptAvailable: boolean | null
  crossAccountAccess?: boolean
  downstreamDashboards: boolean
  downstreamInsights: boolean
  sentimentAnalysis?: boolean
  privateMeetings?: boolean
  assistantMeetings?: boolean
  integrations?: AccountIntegrationConfig[]
  multiLanguageSupport?: MultiLanguageSupportConfig
  businessEvent?: boolean
  emailBusinessEvent?: boolean
  businessEventExport?: boolean
  meetingProcessedDoneEmail?: MeetingProcessedDoneEmailConfig
  dataRetentionWarningEmail?: DataRetentionWarningEmailConfig
  meetingPrepEmail?: MeetingPrepEmailConfig
  meetingScorecard?: boolean
  aiMeetingTitleCalculator?: boolean
  lockedIPHierarchyTypes?: LockedIPHierarchyType[]
  assets?: {
    logo?: LogoConfig
  }
  lockTemplates: boolean
  lockMeetingTemplates: boolean
  lockDocumentTypes: boolean
  lockTemplateOverride?: typeof LOCK_USER_OVERRIDE | typeof LOCK_ALL_OVERRIDE
  lockAccountName?: boolean
  billing: BillingConfig
}

export type LockedIPHierarchyType = {
  type: string
  typeId: string
}

export type IntegrationsEnterpriseAccountConfig = {
  restrictions?: IntegrationRestrictions
  integrationSetupConfigs?: IntegrationSetupConfig[]
  oauthAppCreationEnabled?: boolean | null
}

export type OAuthAppsRestrictions = {
  restrictedOAuthApps: string[]
}

export type OAuthAppsEnterpriseAccountConfig = {
  restrictions?: OAuthAppsRestrictions
}

export type IntegrationRestrictions = {
  allowedIntegrations?: string[]
  deniedIntegrations: string[]
}

export type IntegrationSetupConfig = {
  integration?: string
  url?: string
  openInNewTab?: boolean
}

export type MeetingSummaryEnterpriseAccountConfig = {
  disableComments: boolean
}

export type ComplianceAccountConfig = {
  complianceNotice?: ComplianceNotice
  userConsentTrackingEnabled?: boolean
  userConsentManualOverride?: boolean
  consentApprovalCheck?: boolean
  exportConfirmation?: ExportConfirmationConfig
  integrationExportComplianceMessage?: IntegrationExportComplianceMessageConfig
  emailComplianceMessage?: EmailComplianceMessageConfig
  pdfComplianceMessage?: PdfComplianceMessageConfig
  copyComplianceMessage?: CopyComplianceMessageConfig
  inPersonComplianceMessage?: InPersonComplianceMessageConfig
  userConsentManualConfigs: UserConsentManualConfigs
}

export type ConsentWarningTextConfig = {
  enabled: boolean
  text: string
}

export type EvidenceUploadConfig = {
  enabled: boolean
  setting: ConsentUploadSetting
}
export type CommentUploadConfig = {
  enabled: boolean
  setting: ConsentUploadSetting
}

export type UserConsentManualConfigs = {
  consentWarningText: ConsentWarningTextConfig
  evidenceUpload: EvidenceUploadConfig
  commentUpload: CommentUploadConfig
  meetingLevelConsent: boolean
}

export type AuditLogConfig = {
  enabled: boolean
}

export type BillingConfig = {
  featureAdvertisementEnabled: boolean
}

export type EffectiveAccountConfig = {
  complianceConfig: ComplianceAccountConfig
  region?: AccountRegion
  auditLogConfigs?: AuditLogConfig
  piiRedactionCategories: string[]
  piiRedactionEntities: string[]
  restrictedSessionDelete: boolean
  restrictedSessionDeleteGlobal: boolean
  restrictedSessionTrimming: boolean
  restrictedSessionTrimmingGlobal: boolean
  downloadTranscriptAvailable: boolean
  crossAccountAccess: boolean
  downstreamDashboards: boolean
  downstreamInsights: boolean
  sentimentAnalysis: boolean
  crossClientQueryUserExecution: boolean
  privateMeetings: boolean
  assistantMeetings: boolean
  integrations?: AccountIntegrationConfig[]
  multiLanguageSupport: MultiLanguageSupportConfig
  businessEvent: boolean
  emailBusinessEvent: boolean
  businessEventExport: boolean
  meetingProcessedDoneEmail: MeetingProcessedDoneEmailConfig
  dataRetentionWarningEmail: DataRetentionWarningEmailConfig
  meetingPrepEmail: MeetingPrepEmailConfig
  crossClientEvaluationEmail: CrossClientEvaluationEmailConfig
  meetingScorecard?: boolean
  aiMeetingTitleCalculator: boolean
  assets?: {
    logo?: LogoConfig
  }
  billing: BillingConfig
}

export enum AccountIntegrationConfigMappingType {
  SESSION = 'session',
  NOTE = 'note',
  RESULT = 'result',
  CONTACT = 'contact',
  CONTACT_FIELDS = 'contactFields',
  DOCUMENT_SUMMARY = 'documentSummary',
  TASK = 'task',
  ADDITIONAL_OBJECTS = 'additionalObjects',
  BUSINESS_EVENTS = 'businessEvents',
  CUSTOM_OBJECTS = 'customObjects',
}

export enum DescriptorType {
  FIELD = 'field',
  VARIABLE = 'variable',
  STATIC = 'static',
}

export enum FieldUpdateMode {
  APPEND = 'append',
  PREPEND = 'prepend',
  OVERWRITE = 'overwrite',
  CREATE_ONLY = 'create_only',
}

export type Condition = {
  subject: ConditionValue
  operator: FilterOperator
  source?: ConditionValue | null
}

export type ConditionGroup = {
  operator?: GroupOperator | null
  conditions?: Condition[]
  returnValue?: ConditionValue | null
  fallback?: ConditionGroup | ConditionValue | null
}

export type FieldDescriptor = {
  value?: string | null
  type: DescriptorType
  valueType?: string | null
  valueFormat?: string | null
  updateMode?: FieldUpdateMode | null
  conditions?: ConditionGroup | null
  defaultValue?: string | null
}

export enum GroupOperator {
  AND = 'and',
  OR = 'or',
}

export enum FilterOperator {
  EQ = 'eq',
  NE = 'ne',
  GT = 'gt',
  LT = 'lt',
  GE = 'ge',
  LE = 'le',
  IN = 'in',
}

export const OPERATOR_OPTIONS = [
  { id: 'eq', label: 'Equals' },
  { id: 'ne', label: 'Not Equals' },
  { id: 'gt', label: 'Greater Than' },
  { id: 'lt', label: 'Less Than' },
  { id: 'ge', label: 'Greater or Equal' },
  { id: 'le', label: 'Less or Equal' },
  { id: 'in', label: 'In' },
] as const

export type ConditionValue = {
  value?: string | null
  type: DescriptorType
  object?: string | null
  field?: string | null
  valueType?: string | null
  valueFormat?: string | null
}

export type FilterExpression = {
  field: string
  operator: FilterOperator
  source: ConditionValue
  valueType?: string | null
  valueFormat?: string | null
}

export type Filter = {
  name?: string | null
  groupOperator: GroupOperator
  expressions: FilterExpression[]
}

export type FilterGroup = {
  groupOperator: GroupOperator
  filters: Filter[]
}

export type ContactSync = {
  filterGroup?: FilterGroup | null
  ownerFilterGroup?: FilterGroup | null
  applyOwnerFilter?: boolean | null
}

export enum Direction {
  ASC = 'ASC',
  DESC = 'DESC',
}

export type OrderBy = {
  field: string
  direction: Direction
}

export type OrderBySchema = {
  field: string
  direction: Direction
}

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  FETCH = 'fetch',
}

export type RelatedObject = {
  include?: boolean
  dependency?: string[]
  limit?: number | null
  outputFields?: { [key: string]: string }[]
  orderBy?: OrderBy | null
  filters?: Filter[] | null
  fields?: { [key: string]: FieldDescriptor[] } | null
  operationType?: OperationType
  isHousehold?: boolean
  primaryIdAttribute?: string | null
}

export type Relationship = {
  object: string
  field: string
  valueType?: string | null
  valueFormat?: string | null
}

export type RelationshipTarget = Relationship

export type RelationshipSource = Relationship & {
  filters?: Filter[] | null
}

export type ContactLink = {
  source: RelationshipSource
  target: RelationshipTarget
}

export enum MappingType {
  PREDEFINED = 'PREDEFINED',
  CUSTOM = 'CUSTOM',
}

export type FieldConstraint = {
  maxLength?: number | null
  valueType?: string | null
  valueFormat?: string | null
}

export type UpdateAdditionalInformation = {
  title?: string
  idKey?: string
}

export type DetailedCRMAccountIntegrationMapping = {
  objectName: string
  displayName?: string | null
  fields?: { [key: string]: FieldDescriptor[] }
  additionalInformation?: { [key: string]: unknown }
  filterGroup?: FilterGroup | null
  sync?: ContactSync
  relatedObjects?: { [key: string]: RelatedObject }
  operationType?: OperationType
  contactLinks?: ContactLink[]
  mappingType?: MappingType
  constraints?: { [key: string]: FieldConstraint }
  limit?: number | null
  isHousehold?: boolean | null
}

type BaseCustomObjectFieldMapping = {
  objectName: string
  description?: string | null
}

export type PlainCustomObjectFieldMapping = BaseCustomObjectFieldMapping & {
  type: 'field'
}

export type ReferenceCustomObjectFieldMapping = BaseCustomObjectFieldMapping & {
  type: 'reference'
  referenceObjectName: string
  referenceFields?: string[] | null
}

export type CustomObjectFieldMapping = PlainCustomObjectFieldMapping | ReferenceCustomObjectFieldMapping

export type CustomObjectMapping = {
  objectName: string
  displayName?: string | null
  fields: CustomObjectFieldMapping[]
  contactLinks?: ContactLink[] | null
  contactLinkField?: string | null
  referenceFields: string[]
  filterGroup?: FilterGroup | null
  isHousehold?: boolean | null
}

export type PlanningAccountIntegrationConfig = {
  type: 'planning'
  serviceId: string
  loginUrl: string | null
}

export type CRMAccountIntegrationConfig = {
  type: 'crm'
  serviceId: string
  loginUrl: string | null
  mappings: IntegrationMappings
  isCustom?: boolean | null
  enableNoteExport: boolean
  enableFullMeetingExport: boolean
  isSyncDisabled?: boolean
}

export type IntegrationMappings = {
  [AccountIntegrationConfigMappingType.CONTACT]?: DetailedCRMAccountIntegrationMapping | null
  [AccountIntegrationConfigMappingType.CONTACT_FIELDS]?: DetailedCRMAccountIntegrationMapping | null
  [AccountIntegrationConfigMappingType.SESSION]?: DetailedCRMAccountIntegrationMapping | null
  [AccountIntegrationConfigMappingType.TASK]?: DetailedCRMAccountIntegrationMapping | null
  [AccountIntegrationConfigMappingType.NOTE]?: DetailedCRMAccountIntegrationMapping | null
  [AccountIntegrationConfigMappingType.RESULT]?: DetailedCRMAccountIntegrationMapping | null
  [AccountIntegrationConfigMappingType.DOCUMENT_SUMMARY]?: DetailedCRMAccountIntegrationMapping | null
  [AccountIntegrationConfigMappingType.ADDITIONAL_OBJECTS]?: DetailedCRMAccountIntegrationMapping[] | null
  [AccountIntegrationConfigMappingType.BUSINESS_EVENTS]?: Record<BusinessEventType, DetailedCRMAccountIntegrationMapping>
  [AccountIntegrationConfigMappingType.CUSTOM_OBJECTS]?: CustomObjectMapping[] | null
}

export type MeetingProviderAccountIntegrationConfig = {
  type: 'meeting_provider'
  serviceId: string
  sharedPhoneLineEnabled?: boolean | null
  allowedExternalIds: string[]
  defaultProvider: string | null
  defaultLocation: string | null
  adhocAutoJoinEnabled?: boolean | null
  strictJoinEnabled?: boolean | null
}

export type DocumentAccountIntegrationConfig = {
  type: 'document'
  serviceId: string
  externalId?: string | null
  externalAccountId?: string | null
}

export type AccountIntegrationConfig =
  | CRMAccountIntegrationConfig
  | PlanningAccountIntegrationConfig
  | MeetingProviderAccountIntegrationConfig
  | DocumentAccountIntegrationConfig

export type AccountUpdatePayload = {
  name?: string
  config?: {
    anonymousAccess: AnonymousAccessType
  }
}

export interface ComplianceFileInfo {
  fileId: string
  fileName: string
  fileSize: number
}

export interface ComplianceNotice {
  audioEnabled: boolean
  imageEnabled: boolean
  chatEnabled: boolean
  audioFileOriginal: Nullable<ComplianceFileInfo>
  imageFileOriginal: Nullable<ComplianceFileInfo>
  audioFileConverted: Nullable<ComplianceFileInfo>
  imageFileConverted: Nullable<ComplianceFileInfo>
  chatMessage: string
  imageOverrideEnabled?: boolean | null
  audioPlayOnceOnlyEnabled?: boolean | null
  inPersonAudioEnabled: boolean
  inPersonAudioFileOriginal: Nullable<ComplianceFileInfo>
  inPersonAudioFileConverted: Nullable<ComplianceFileInfo>
}

export type EmailAction = 'generateReply' | 'pushToDraft' | 'doNotReply'

export interface EmailConfig {
  calculateEmailTypeForConsent: UserConsentState[]
  emailActionsByEmailType: { [key: string]: EmailAction }
  uncategorizedEmailAction: EmailAction
  userRolesByEmailType: { [key: string]: string[] }
}

export interface MultiLanguageSupportConfig {
  enabled: boolean
  defaultLanguage: MeetingLanguage
  extraLanguages: MeetingLanguage[]
  supportedLanguages: MeetingLanguage[]
}

export interface MeetingPrepEmailNotificationConfig {
  meetingOwner: boolean
  teamMemberIds: string[]
}

export interface MeetingPrepEmailConfig {
  enabled: boolean
  includeInfo: 'linkOnly' | 'linkAndSummary'
  notifications: MeetingPrepEmailNotificationConfig
  includeTitleInSubject: boolean
}

export interface MeetingProcessedDoneEmailNotificationConfig {
  teamMemberIds: string[]
}

export interface MeetingProcessedDoneEmailConfig {
  includeInfo: 'linkOnly' | 'linkAndMeetingInfo' | 'linkAndMeetingInfoAndSummary'
  notifications: MeetingProcessedDoneEmailNotificationConfig
  includeTitleInSubject: boolean
}

export interface CrossClientEvaluationEmailNotificationConfig {
  teamMemberIds: string[]
}

export interface CrossClientEvaluationEmailConfig {
  enabled: boolean
  notifications: CrossClientEvaluationEmailNotificationConfig
}

export interface DataRetentionWarningEmailNotificationConfig {
  meetingOwner: boolean
  teamMemberIds: string[]
}

export interface DataRetentionWarningEmailConfig {
  enabled: boolean
  frequency: 'daily' | 'weekly'
  includeInfo: 'linkOnly' | 'linkAndTitle'
  notifications: DataRetentionWarningEmailNotificationConfig
}
